import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui";
import { AgentAvatar } from "@/workshop/components/canvas/agent-avatar";

/**
 * Workshop: Notification toast — ephemeral slide-in for real-time alerts.
 *
 * Appears when a WebSocket event fires (approval request, workflow done, error).
 * Auto-dismisses after ~5s. Positioned top-right of the canvas.
 */

export type ToastType = "approval" | "completed" | "failed" | "info";

interface NotificationToastProps {
  type: ToastType;
  agentLetter: string;
  agentColor: string;
  agentName: string;
  title: string;
  /** Optional description — e.g. "Send email to client@example.com" */
  description?: string;
  /** Risk level for approval toasts */
  riskLevel?: "low" | "medium" | "high";
  onView?: () => void;
  onDismiss?: () => void;
  /** Whether dismissing (slide-out animation) */
  dismissing?: boolean;
  className?: string;
  "data-testid"?: string;
}

const TYPE_CONFIG: Record<
  ToastType,
  { label: string; icon: React.ReactNode; accent: string; bg: string }
> = {
  approval: {
    label: "Approval needed",
    icon: <Icons.AlertTriangle className="h-3.5 w-3.5" />,
    accent: "text-warning",
    bg: "bg-warning/[0.06]",
  },
  completed: {
    label: "Completed",
    icon: <Icons.Check className="h-3.5 w-3.5" />,
    accent: "text-emerald-400",
    bg: "bg-emerald-400/[0.06]",
  },
  failed: {
    label: "Failed",
    icon: <Icons.X className="h-3.5 w-3.5" />,
    accent: "text-red-400",
    bg: "bg-red-400/[0.06]",
  },
  info: {
    label: "Update",
    icon: <Icons.Info className="h-3.5 w-3.5" />,
    accent: "text-blue-400",
    bg: "bg-blue-400/[0.06]",
  },
};

const RISK_COLORS: Record<string, string> = {
  low: "text-success",
  medium: "text-warning",
  high: "text-danger",
};

export function NotificationToast({
  type,
  agentLetter,
  agentColor,
  agentName,
  title,
  description,
  riskLevel,
  onView,
  onDismiss,
  dismissing = false,
  className,
  "data-testid": testId = "notification-toast",
}: NotificationToastProps) {
  const config = TYPE_CONFIG[type];

  return (
    <div
      className={cn(
        "pointer-events-auto w-80 rounded-xl border border-glass-stroke shadow-glass backdrop-blur-md",
        "animate-in slide-in-from-top-2 fade-in duration-300",
        dismissing && "animate-out slide-out-to-right fade-out duration-200",
        config.bg,
        className,
      )}
      data-testid={testId}
    >
      {/* Top bar — type label + dismiss */}
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
        <div className="flex items-center gap-1.5">
          <span className={cn("shrink-0", config.accent)}>{config.icon}</span>
          <span
            className={cn(
              "text-[10px] font-semibold uppercase tracking-wide",
              config.accent,
            )}
          >
            {config.label}
          </span>
          {type === "approval" && riskLevel && (
            <span
              className={cn(
                "text-[9px] font-medium",
                RISK_COLORS[riskLevel],
              )}
            >
              · {riskLevel} risk
            </span>
          )}
        </div>
        <button
          type="button"
          className="flex h-4 w-4 items-center justify-center rounded transition-colors hover:bg-glass-fill-heavy"
          onClick={onDismiss}
          data-testid="toast-dismiss"
          aria-label="Dismiss"
        >
          <Icons.X className="h-2.5 w-2.5 text-muted" />
        </button>
      </div>

      {/* Content */}
      <div className="flex items-start gap-2.5 px-3 pb-2.5">
        <div className="mt-0.5 shrink-0">
          <AgentAvatar
            letter={agentLetter}
            color={agentColor}
            size="sm"
            hideStatus
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-[11px] font-medium text-primary">{title}</span>
          {description && (
            <p className="truncate text-[10px] text-secondary">{description}</p>
          )}
          <span className="text-[9px] text-muted">{agentName}</span>
        </div>
      </div>

      {/* Action bar */}
      {onView && (
        <div className="border-t border-glass-stroke-subtle px-3 py-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-[10px] text-accent"
            onClick={onView}
            data-testid="toast-view-btn"
          >
            View
          </Button>
        </div>
      )}
    </div>
  );
}

export type { NotificationToastProps };
